<template>
    <div class="promo-code-section px-4 pb-4">
        <div class="promo-code-input-group flex items-center justify-between">
            <input 
                :id="couponInputId" 
                v-model.trim="couponCode" 
                type="text" 
                placeholder="Enter promo code"
                class="promo-code-input mr-2 text-base text-[var(--charcoal-dark)] border border-[var(--bone)] focus:border-[var(--primary)] rounded-full h-9 px-3 py-1 bg-white w-full outline-0" 
                @keyup="resetCoupon"
                :disabled="hasCouponApplied">
            <button 
                :id="applyButtonId"
                :class="['promo-code-button btn-primary !py-1.5 whitespace-nowrap', hasCouponApplied ? 'applied' : '']"
                :disabled="hasCouponApplied || processing" 
                data-track="apply_coupon"
                @click="applyCoupon">
                <span v-if="processing">Applying...</span>
                <span v-else-if="hasCouponApplied">✓ Applied</span>
                <span v-else>Apply</span>
            </button>
        </div>
        <div class="promo-code-messages">
            <p v-for="(message, index) in successMessages" :key="`success-${index}`" class="message-success text-sm text-[var(--success)] border border-[var(--bone)] py-1 px-3 rounded-full mt-2">
                {{ message }}
            </p>
            <p v-for="(error, index) in errorMessages" :key="`error-${index}`" class="message-error test-sm text-[var(--error)] border border-[var(--bone)] py-1 px-3 rounded-full mt-2">
                {{ error }}
            </p>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';

export default {
    name: 'Discount',
    props: {
        item: { type: Object, required: true },
        allItem: { type: Object, required: true },
        globalTotalItem: { type: Object, required: true }
    },
    data() {
        return {
            couponCode: this.item.code || '',
            successMessages: [],
            errorMessages: [],
            processing: false
        };
    },
    computed: {
        hasCouponApplied() {
            return !!this.item.tour_promotion_id;
        },
        couponInputId() {
            return `couponCode-${this.item.tour_slot_id}`;
        },
        applyButtonId() {
            return `applyCouponButton-${this.item.tour_slot_id}`;
        }
    },
    methods: {
        resetCoupon() {
            this.successMessages = [];
            this.errorMessages = [];
            Object.assign(this.item, {
                tour_promotion_id: '',
                discount2_value: 0,
                discount2_percentage: 0,
                subtotal: Number(this.item.before_discount_subtotal),
                fees: Number(this.item.before_discount_fees),
                total: Number(this.item.before_discount_total)
            });
            this.updateCart();
        },

        async applyCoupon() {
            if (this.processing || !this.couponCode) return;

            this.processing = true;
            const loader = this.$loading.show();
            this.clearMessages();

            if (!this.couponCode) {
                this.errorMessages.push('Please enter a promo code to receive a discount.');
                this.hideLoader(loader);
                return;
            }

            try {
                const { data } = await axios.get(
                    `/apply-coupon/${this.item.package_id}/${this.item.tour_slot_id}/${this.couponCode}`
                );
                const promocode = data.data;
                const { subtotal, discount } = this.calculateDiscount(promocode, this.item.subtotal);

                if (subtotal <= 0) {
                    this.errorMessages.push('Your coupon code is not valid.');
                } else {
                    this.applyDiscount(promocode, subtotal, discount, data.message);
                    this.updateCart();
                    this.$emit('update-items', cloneDeep(this.allItem));
                }
            } catch (error) {
                this.handleError(error);
            } finally {
                this.hideLoader(loader);
            }
        },

        calculateDiscount(promocode, subtotal) {
            const isPercent = promocode.discount_value_type === 'Percent';
            const discountValue = Number(promocode.discount_value);
            let discount = isPercent
                ? this.roundout((subtotal * discountValue) / 100)
                : discountValue;
            const newSubtotal = subtotal - discount;

            return {
                subtotal: Number(newSubtotal.toFixed(2)),
                discount: Number(discount.toFixed(2))
            };
        },

        applyDiscount(promocode, subtotal, discount, successMessage) {
            const fees = this.roundout(subtotal * this.item.service_commission / 100, 2);
            Object.assign(this.item, {
                tour_promotion_id: promocode.id,
                discount2_value: discount,
                discount2_percentage: promocode.discount_value_type === 'Percent' ? Number(promocode.discount_value) : 0,
                subtotal,
                fees: Number(fees),
                total: this.roundout(subtotal + fees)
            });
            this.successMessages.push(successMessage);
        },

        updateCart() {
            const totals = Object.values(this.allItem).reduce((acc, item) => ({
                subtotal: acc.subtotal + Number(item.subtotal),
                discount: acc.discount + Number(item.discount2_value),
                fees: acc.fees + Number(item.fees),
                addons_total: acc.addons_total + Number(item.addons_total),
                addons_fee: acc.addons_fee + Number(item.addons_fee),
                total: acc.total + Number(item.total)
            }), {
                subtotal: 0,
                discount: 0,
                fees: 0,
                addons_total: 0,
                addons_fee: 0,
                total: 0
            });

            Object.keys(totals).forEach(key => {
                totals[key] = this.roundout(totals[key]);
            });

            Object.assign(this.globalTotalItem, totals);
        },

        handleError(error) {
            this.couponCode = '';
            Object.assign(this.item, {
                tour_promotion_id: '',
                discount2_value: 0,
                discount2_percentage: 0
            });
            this.errorMessages.push(
                error.response?.data?.message || 'An error occurred'
            );
        },

        clearMessages() {
            this.successMessages = [];
            this.errorMessages = [];
        },

        hideLoader(loader) {
            this.processing = false;
            loader.hide();
        },

        roundout(amount, places = 2) {
            if (places < 0) places = 0;
            let factor = Math.pow(10, places);
            return Math.round(amount * factor) / factor;
        }
    }
};
</script>